import {AlertProductRecalculationRequired} from "./AlertProductRecalculationRequired";
import {useContext, useState} from "react";
import {ProjectContext} from "./ProjectProvider";
import {AlternativeContext} from "./quote/alternative/AlternativeContext";
import {useProjectId} from "./shopdrawing/ProjectName";
import {useAlternative} from "./quote/alternative/Alternative";
import {useLocalStorage} from "../../misc/LocalSession";
import {useEventEmitter} from "../../misc/UseEventEmitter";
import {Menu, MenuItem} from "@mui/material";
import {useReloader} from "./shopdrawing/hardware/ReloadContext";
import CalculateIcon from '@mui/icons-material/Calculate';
import {HeaderActionButton} from "./shopdrawing/HeaderActionButton";
import {grey} from "@mui/material/colors";
import {HistoryDialog} from "./prodrecalc/HistoryDialog";
import {DynamicErrorsDialog} from "./prodrecalc/DynamicErrorsDialog";

export function useAlertProductRecalcState() {
    const projCtx = useContext(ProjectContext);
    const altCtx = useContext(AlternativeContext);
    const hwReload = useReloader();

    const projectId = useProjectId();
    const altId = useAlternative();

    const [hide, setHide] = useLocalStorage<boolean>(`product-recalc-proj=${projectId}-alt=${altId}`, false);

    const alt = useEventEmitter(altCtx.alternative);
    let show = false;

    if(altCtx.valid) {
        show = alt?.needsRecalculateProducts || false;
    } else if(projCtx.valid) {
        show = projCtx.value.needsRecalculateProducts;
    }

    // also include dynamic in the error set
    const hasDynamicErr = projCtx.valid && projCtx.value.hasDynamicProductErrors;

    let loading = false;
    if(!projCtx.value) loading = true;
    if(altCtx.valid && !alt) loading = true;

    const onDone = () => {
        setHide({
            value: true,
            expiresAt: Date.now() + 5 * 1000,
        });

        projCtx.reload();
        hwReload.current()

        if(altCtx.valid) {
            altCtx.reload();
        }
    };

    const onHide = () => {
        setHide({
            value: true,
            expiresAt: Date.now() + 5 * 60 * 1000, // 5 minutes
        });
    }

    return {
        loading,
        show,
        hasDynamicErr,
        hide,
        onHide,
        onUnHide: () => setHide(null),
        projectId,
        altId,
        onDone,
    }
}

export function AlertProductRecalcHeader() {

    const [showMenu, setShowMenu] = useState<HTMLElement|null>(null);
    const [showHistory, setShowHistory] = useState(false);
    const [showDynamicErrors, setShowDynamicErrors] = useState(false);

    const {show, hide, hasDynamicErr, projectId, altId, onHide, onUnHide, onDone, loading} = useAlertProductRecalcState();
    if(loading) return null;

    return (
        <>
            <HeaderActionButton
                label="Products"
                icon={<CalculateIcon fontSize="inherit" color={grey["800"] as any} />}
                badgeContent={(show || hasDynamicErr) ? "Errors" : null}
                onClick={(e) => {
                    setShowMenu(e.currentTarget)
                }}
            />

            {showMenu && <Menu onClose={() => setShowMenu(null)} open anchorEl={showMenu}>
                {show && <MenuItem onClick={() => {
                    setShowMenu(null)
                    onUnHide();
                }}>Fix Errors</MenuItem>}
                <MenuItem onClick={() => {
                    setShowMenu(null)
                    setShowHistory(true);
                }}>
                    Product Change History
                </MenuItem>
                <MenuItem onClick={() => {
                    setShowMenu(null)
                    setShowDynamicErrors(true);
                }}>
                    Dynamic Errors
                </MenuItem>
            </Menu>}

            {showHistory && <HistoryDialog onClose={() => setShowHistory(false)} />}
            {showDynamicErrors && <DynamicErrorsDialog onClose={() => setShowDynamicErrors(false)} />}

            {!hide && show && <AlertProductRecalculationRequired
                project={projectId}
                alternative={altId}
                onHide={onHide}
                onDone={onDone}
            />}
        </>
    )
}